import { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import type { FragmentType, TrackAudioInfoFragment } from '@graphql/reactQuery';
import {
  FeedPostModalMainSoundFragmentDoc,
  getFragment,
  PlayerTrackInfoDocument,
  ReleaseTrackAudioInfoFragmentDoc,
  setQueryData,
  TrackAudioInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { isHlsSupported } from '../store/audio/hls';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseTrackAudioInfo on Release {
    id
    title
    webappUri
    type
    mintStartTimestamp
    listeningParty
    spotifyTrackId
    artist {
      id
      name
      webappUri
      user {
        id
        username
      }
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    animatedCoverImage {
      id
      url
    }
    ...FeedPostModalMainSound
  }

  fragment TrackAudioInfo on Track {
    id
    audio {
      audio128k {
        id
        url
      }
      audioHls {
        id
        url
      }
      audioOriginal {
        id
        url
      }
    }
  }
`);

type AudioMedia = TrackAudioInfoFragment['audio'];

export function computeAudioUrl(audioMedia: AudioMedia): string {
  if (isHlsSupported()) {
    return audioMedia?.audioHls?.url || audioMedia?.audio128k?.url || audioMedia.audioOriginal.url;
  }

  return audioMedia?.audio128k?.url || audioMedia.audioOriginal.url;
}

type TrackAudioInfoProp = Maybe<FragmentType<TrackAudioInfoFragmentDoc>>;
type ReleaseTrackAudioInfoProp = Maybe<
  FragmentType<ReleaseTrackAudioInfoFragmentDoc> & FragmentType<FeedPostModalMainSoundFragmentDoc>
>;

export function parseTrackAudioInfo(track: TrackAudioInfoProp) {
  return getFragment(TrackAudioInfoFragmentDoc, track) || null;
}

export function parseReleaseTrackAudioInfo(release: ReleaseTrackAudioInfoProp) {
  return getFragment(ReleaseTrackAudioInfoFragmentDoc, release) || null;
}

export function parseFeedPostModalMainSound(release: ReleaseTrackAudioInfoProp) {
  return getFragment(FeedPostModalMainSoundFragmentDoc, release) || null;
}

export function useReleaseTrackAudioInfo({
  track,
  release,
}: {
  track: TrackAudioInfoProp;
  release: ReleaseTrackAudioInfoProp;
}) {
  const trackInfo = parseTrackAudioInfo(track);
  const releaseInfo = parseReleaseTrackAudioInfo(release);
  const feedPostInfo = parseFeedPostModalMainSound(release);

  useSafeLayoutEffect(() => {
    if (!trackInfo || !track || !release || !releaseInfo || !feedPostInfo) return;

    setQueryData(
      {
        query: PlayerTrackInfoDocument,
        variables: {
          trackId: trackInfo.id,
        },
      },
      previousData => {
        if (previousData) return previousData;

        return {
          data: {
            track: {
              id: trackInfo.id,
              release: {
                ...release,
                id: releaseInfo.id,
                listeningParty: releaseInfo.listeningParty,
              },
            },
          },
        };
      },
    );
  }, [release, releaseInfo, feedPostInfo]);

  const trackInfoAudio = trackInfo?.audio;

  const trackAudioUrl = useMemo(
    () => (trackInfoAudio ? computeAudioUrl(trackInfoAudio) : null),
    [trackInfoAudio],
  );

  const trackId = trackInfo?.id;

  if (trackId && releaseInfo && release && trackInfoAudio && trackAudioUrl && feedPostInfo) {
    return {
      ok: true as const,
      releaseInfo,
      trackInfoAudio,
      trackAudioUrl,
      trackId,
      feedPostInfo,
    };
  }

  return {
    ok: false as const,
    releaseInfo,
    trackInfoAudio,
    trackAudioUrl,
    trackId,
    feedPostInfo,
  };
}
